import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import AppleLogin from 'react-apple-login';

function migrateLegacySession() {
  const keys = ["token", "refreshToken", "user"];
  const hasSessionValues = keys.some((key) => sessionStorage.getItem(key));
  if (hasSessionValues) {
    return;
  }

  keys.forEach((key) => {
    const legacyValue = localStorage.getItem(key);
    if (legacyValue) {
      sessionStorage.setItem(key, legacyValue);
      localStorage.removeItem(key);
    }
  });
}

function readStoredSession() {
  migrateLegacySession();
  return {
    token: sessionStorage.getItem("token"),
    refreshToken: sessionStorage.getItem("refreshToken"),
    user: sessionStorage.getItem("user")
  };
}

export default function Login() {
  const [error, setError] = useState("");
  /** @type {"idle"|"linking"|"linked"|"failed"} */
  const [linkState, setLinkState] = useState("idle");
  const [linkMessage, setLinkMessage] = useState("");
  const [authSuccess, setAuthSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Parse device code from URL once
  const deviceCode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("code")?.trim().toUpperCase() || null;
  }, []);

  // If already logged in and a device code exists, auto-complete the device link
  useEffect(() => {
    const { token: storedToken, refreshToken: storedRefreshToken, user: storedUser } = readStoredSession();

    if (storedToken && storedUser && !deviceCode) {
      setAuthSuccess(true);
      setSuccessMessage("You are signed in. You can close this page.");
      return;
    }

    // Auto-complete device link if we're already logged in and have a code
    if (storedToken && deviceCode) {
      (async () => {
        try {
          await maybeCompleteDeviceLink(storedToken, storedRefreshToken);
          setAuthSuccess(true);
          setSuccessMessage("Success. Your TV is now linked.");
        } catch (e) {
          setLinkState("failed");
          setLinkMessage(
            e?.response?.data?.message || "Link failed. Please try again."
          );
        }
      })();
    }
  }, [deviceCode]);

  const maybeCompleteDeviceLink = async (token, refreshToken) => {
    if (!deviceCode) return;
    setLinkState("linking");
    setLinkMessage("Linking your TV…");
    await axios.post(
      "/api/device/complete",
      { code: (deviceCode || "").toUpperCase() },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(refreshToken ? { "X-Refresh-Token": refreshToken } : {})
        }
      }
    );
    setLinkState("linked");
    setLinkMessage("Linked! You can return to your TV.");
  };

  const handleAppleResponse = async (response) => {
    try {
      const res = await fetch('/api/auth/apple/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: response?.authorization?.code,
          id_token: response?.authorization?.id_token
        }),
      });
      const data = await res.json();

      if (res.ok && data.token) {
        sessionStorage.setItem('token', data.token);
        if (data.refreshToken) {
          sessionStorage.setItem('refreshToken', data.refreshToken);
        } else {
          sessionStorage.removeItem('refreshToken');
        }
        sessionStorage.setItem('user', JSON.stringify(data.user || {}));
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        if (deviceCode) {
          try {
            await maybeCompleteDeviceLink(data.token, data.refreshToken);
            setSuccessMessage("Success. Your TV is now linked.");
          } catch (e) {
            setLinkState("failed");
            setLinkMessage(
              e.response?.data?.message || "TV link failed after Apple login."
            );
            setSuccessMessage("Logged in successfully, but TV linking failed.");
          }
        } else {
          setSuccessMessage("Logged in successfully.");
        }
        setAuthSuccess(true);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (e) {
      setError('Apple login failed');
    }
  };

  const handleSignOut = async () => {
    const refreshToken = sessionStorage.getItem("refreshToken") || localStorage.getItem("refreshToken");

    if (refreshToken) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch (_) {
        // Local cleanup still proceeds if the revoke request fails.
      }
    }

    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    setAuthSuccess(false);
    setSuccessMessage("");
    setError("");
    setLinkState("idle");
    setLinkMessage("");
  };

  if (authSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-[#0f2133] to-[#133A7A] text-white px-4">
        <div className="w-full max-w-md bg-white/10 backdrop-blur-md border border-white/20 shadow-xl p-8 rounded text-center">
          <h1 className="text-2xl font-bold text-[#00AEEF] mb-2">Success</h1>
          <p className="text-white/90 mb-6">{successMessage || "Logged in successfully."}</p>
          <p className="text-sm text-white/70 mb-6">You can now close this page.</p>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full bg-white/20 hover:bg-white/30 text-white font-semibold py-2 px-4 rounded transition"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-[#0f2133] to-[#133A7A]">
      {deviceCode && (
        <div className="mb-4 w-full max-w-sm">
          <div className={`rounded px-3 py-2 text-sm ${
            linkState === "linked" ? "bg-green-600/30 text-green-200 border border-green-400/50" :
            linkState === "failed" ? "bg-red-600/30 text-red-200 border border-red-400/50" :
            "bg-blue-600/30 text-blue-100 border border-blue-400/50"
          }`}>
            <div className="font-semibold">
              Linking TV (code: {deviceCode})
            </div>
            <div className="opacity-90">
              {linkMessage || "Sign in to link this TV to your account."}
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-sm bg-white/10 backdrop-blur-md border border-white/20 shadow-xl p-8 rounded">
        <h2 className="text-2xl mb-1 text-[#00AEEF] font-bold">Welcome Back!</h2>
        <p className="mb-4 text-white/80">Sign in with Apple to continue.</p>

        {error && <div className="mb-2 text-red-400">{error}</div>}

        <div className="flex justify-center">
          {process.env.REACT_APP_APPLE_CLIENT_ID && process.env.REACT_APP_APPLE_REDIRECT_URI && (
            <AppleLogin
              clientId={process.env.REACT_APP_APPLE_CLIENT_ID}
              redirectURI={process.env.REACT_APP_APPLE_REDIRECT_URI}
              responseType="code id_token"
              responseMode="form_post"
              usePopup={true}
              callback={handleAppleResponse}
              theme="dark"
              type="sign in"
              aria-label="Sign in with Apple"
              render={renderProps => (
                <button
                  type="button"
                  onClick={renderProps.onClick}
                  className="flex items-center gap-2 w-full bg-black hover:bg-gray-900 text-white font-semibold py-2 px-4 rounded transition shadow disney-btn-apple"
                  style={{ justifyContent: "center" }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M16.49 10.44c-.02-2.12 1.73-3.13 1.8-3.18-1-1.46-2.53-1.66-3.07-1.69-1.3-.13-2.53.77-3.19.77-.66 0-1.69-.75-2.78-.73-1.43.02-2.75.84-3.48 2.13-1.48 2.56-.38 6.36 1.05 8.45.7 1.01 1.54 2.15 2.65 2.11 1.07-.04 1.47-.68 2.76-.68 1.29 0 1.64.68 2.78.66 1.15-.02 1.87-1.02 2.56-2.03.8-1.16 1.12-2.29 1.14-2.34-.03-.02-2.18-.84-2.2-3.33zM13.51 4.44c.59-.71.99-1.7.88-2.69-.86.03-1.9.57-2.52 1.28-.55.62-1.04 1.61-.86 2.56.95.07 1.91-.48 2.5-1.15z"/>
                  </svg>
                  <span>Sign in with Apple</span>
                </button>
              )}
            />
          )}
          {(!process.env.REACT_APP_APPLE_CLIENT_ID || !process.env.REACT_APP_APPLE_REDIRECT_URI) && (
            <div className="text-sm text-red-300 text-center">
              Apple sign-in is not configured on this deployment.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
